import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import {
  useScoutEmailSettings,
  useUpdateScoutEmailSettings,
} from '../../hooks/useScoutSettings';
import { ConfirmationDialog } from './ConfirmationDialog';

export const ScoutEmailSettings: React.FC = () => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);

  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError,
    refetch: refetchSettings,
  } = useScoutEmailSettings();

  const { mutate: updateSettings, isPending: isUpdating } =
    useUpdateScoutEmailSettings();

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;

    // 受信拒否（false）に変更する場合は確認ダイアログを表示
    if (!newValue && settings?.acceptsScoutEmails) {
      setPendingValue(newValue);
      setShowConfirmation(true);
      return;
    }

    // 受信許可（true）に変更する場合は即座に実行
    handleUpdateSettings(newValue);
  };

  const handleUpdateSettings = async (acceptsScoutEmails: boolean) => {
    try {
      await updateSettings({
        acceptsScoutEmails,
        confirmationAcknowledged: !acceptsScoutEmails, // 受信拒否時は確認済み
      });

      // 設定更新後に最新の設定を取得
      await refetchSettings();
    } catch (error) {
      // エラーはhook内で処理される
    }
  };

  const handleConfirmChange = () => {
    if (pendingValue !== null) {
      handleUpdateSettings(pendingValue);
    }
    setShowConfirmation(false);
    setPendingValue(null);
  };

  const handleCancelChange = () => {
    setShowConfirmation(false);
    setPendingValue(null);
  };

  if (isLoadingSettings) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (settingsError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{settingsError}</Alert>
        </CardContent>
      </Card>
    );
  }

  const currentSetting = settings?.acceptsScoutEmails ?? true;

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <EmailIcon sx={{ fontSize: 28, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                スカウトメール受信設定
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                企業からのスカウトメールを受信するかどうかを設定できます
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 現在の設定状況 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              現在の設定
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {currentSetting ? (
                <CheckCircleIcon
                  sx={{ fontSize: 20, color: 'success.main', mr: 1 }}
                />
              ) : (
                <BlockIcon sx={{ fontSize: 20, color: 'error.main', mr: 1 }} />
              )}
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {currentSetting
                  ? 'スカウトメールを受信する'
                  : 'スカウトメールを受信しない'}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              {currentSetting
                ? '企業からのスカウトメールを受信できます'
                : '企業からのスカウトメールは届きません'}
            </Typography>
          </Box>

          {/* 設定変更 */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              設定変更
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={currentSetting}
                  onChange={handleToggleChange}
                  disabled={isUpdating}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    スカウトメールを受信する
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    企業からの採用関連メッセージを受け取ります
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>

          {/* 説明 */}
          <Box
            sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', lineHeight: 1.6 }}
            >
              <strong>スカウトメールについて：</strong>
              企業があなたのプロフィールや学習履歴を見て、採用に興味を持った場合に送信されるメッセージです。
              受信を無効にしても、学習機能は引き続きご利用いただけます。
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 確認ダイアログ */}
      <ConfirmationDialog
        open={showConfirmation}
        onClose={handleCancelChange}
        onConfirm={handleConfirmChange}
        loading={isUpdating}
      />
    </>
  );
};
